import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Shield } from 'lucide-react';
import { BancoSponsorBadge, ReemplazadoBadge } from './CvuReemplazoInfo';
import { CvuFiltrosBar } from './CvuFiltrosBar';
import { CvuResumenActivos } from './CvuResumenActivos';
import { esCvuActivo, filtrarCvus } from './cvuUtils';

const ORIGEN_COLOR = {
  Empresa: 'bg-purple-100 text-purple-700',
  'Padrón': 'bg-indigo-100 text-indigo-700',
  'Empresa y padrón': 'bg-teal-100 text-teal-700',
};

const fmt = (v) => (v ? `$${Number(v).toLocaleString('es-AR')}` : '—');

const FilaCvu = ({ c, i }) => (
  <TableRow data-testid={`cvu-unificado-row-${i}`}>
    <TableCell className="font-mono text-xs">{c.numero}</TableCell>
    <TableCell><BancoSponsorBadge banco={c.banco_sponsor} testid={`cvu-unificado-sponsor-${i}`} /></TableCell>
    <TableCell className="text-xs">
      <div className="flex flex-wrap gap-1">
        <Badge className={`text-[10px] ${esCvuActivo(c) ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`cvu-unificado-estado-${i}`}>{esCvuActivo(c) ? 'Activo' : 'Inactivo'}</Badge>
        <ReemplazadoBadge cvu={c} testid={`cvu-unificado-reemplazado-${i}`} />
      </div>
    </TableCell>
    <TableCell className="text-xs" data-testid={`cvu-unificado-reemplaza-${i}`}>
      {c.reemplaza_a ? (
        <div><span className="font-mono">{c.reemplaza_a}</span>{c.motivo_cambio && <p className="text-slate-500">{c.motivo_cambio}</p>}</div>
      ) : '—'}
    </TableCell>
    <TableCell><Badge className={`text-[10px] ${ORIGEN_COLOR[c.origen] || ''}`} data-testid={`cvu-unificado-origen-${i}`}>{c.origen}</Badge></TableCell>
    <TableCell className="text-xs">{c.alias || '—'}</TableCell>
    <TableCell className="text-xs whitespace-nowrap">{(c.limite_diario || c.limite_mensual) ? `${fmt(c.limite_diario)} / ${fmt(c.limite_mensual)}` : '—'}</TableCell>
  </TableRow>
);

export const CvusUnificadosTabla = ({ cvus }) => {
  const [filtros, setFiltros] = useState({ sponsor: 'todos', estado: 'todos' });
  const visibles = filtrarCvus(cvus, filtros);
  return (
    <Card className="p-6 bg-white" data-testid="cvus-unificados-section">
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="text-indigo-600" size={20} />
          CVUs ({cvus.length})
        </h2>
        <Badge className="bg-indigo-50 text-indigo-700 text-xs">Empresa + Padrón</Badge>
      </div>
      <p className="text-xs text-slate-500 mb-3">Lista unificada: CVUs cargados en la empresa y los del padrón para este CUIT, sin duplicar.</p>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <CvuResumenActivos cvus={cvus} testid="empresa-cvus-resumen" />
        <CvuFiltrosBar cvus={cvus} filtros={filtros} onChange={setFiltros} testid="empresa-cvus-filtros" total={visibles.length} />
      </div>
      <div className="overflow-auto rounded-lg border">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              {['CVU', 'Banco sponsor', 'Estado', 'Reemplaza a', 'Origen', 'Alias', 'Límite diario / mensual'].map((h) => (
                <TableHead key={h} className="text-xs">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibles.map((c) => <FilaCvu key={c.numero_limpio || c.numero} c={c} i={cvus.indexOf(c)} />)}
          </TableBody>
        </Table>
        {visibles.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-6" data-testid="empresa-cvus-sin-resultados">Ningún CVU cumple los filtros.</p>
        )}
      </div>
    </Card>
  );
};
